import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type { Note } from '@/types/note';
import type { Paginated } from '@/types/pagination';
import * as notesApi from '../endpoints/notes';
import { ListNotesParams } from '../endpoints/notes';

/**
 * Query keys for notes
 */
export const NOTES_QUERY_KEYS = {
  all: ['notes'] as const,
  lists: () => [...NOTES_QUERY_KEYS.all, 'list'] as const,
  list: (params: ListNotesParams) => [...NOTES_QUERY_KEYS.lists(), params] as const,
  details: () => [...NOTES_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...NOTES_QUERY_KEYS.details(), id] as const,
} as const;

/**
 * Domain-oriented query options for note operations
 * Provides reusable queryOptions that can be used in hooks or prefetching
 */

/**
 * Query options for listing notes with pagination and filtering
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Query options for the notes list
 *
 * @example
 * ```typescript
 * // All my notes
 * const options = notesListQueryOptions();
 *
 * // Notes for a specific business
 * const options = notesListQueryOptions({ business_id: 'business-123' });
 *
 * // Notes for a specific menu item
 * const options = notesListQueryOptions({
 *   business_id: 'business-123',
 *   menu_item_id: 'menu-item-456'
 * });
 * ```
 */
export const notesListQueryOptions = (params: ListNotesParams = {}) =>
  queryOptions({
    queryKey: NOTES_QUERY_KEYS.list(params),
    queryFn: async (): Promise<Paginated<Note>> => {
      return notesApi.getNotes(params);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Infinite query options for paginated notes list
 *
 * @param params - Query parameters for filtering (page is managed by infinite query)
 * @returns Infinite query options for notes
 *
 * @example
 * ```typescript
 * const options = notesInfiniteQueryOptions({ business_id: 'business-123' });
 * ```
 */
export const notesInfiniteQueryOptions = (params: Omit<ListNotesParams, 'page'> = {}) =>
  infiniteQueryOptions({
    queryKey: [...NOTES_QUERY_KEYS.list(params), 'infinite'],
    queryFn: async ({ pageParam }): Promise<Paginated<Note>> => {
      return notesApi.getNotes({ ...params, page: pageParam, per_page: params.per_page ?? 20 });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for fetching a single note by ID
 *
 * @param id - Note ID
 * @returns Query options for the note detail
 *
 * @example
 * ```typescript
 * const options = noteDetailQueryOptions('note-123');
 * ```
 */
export const noteDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: NOTES_QUERY_KEYS.detail(id),
    queryFn: async (): Promise<Note> => {
      return notesApi.getNoteById(id);
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });
